import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Video, Upload, Loader2, Trash2, CheckCircle2 } from "lucide-react";

export default function AdminVideoUploader({ title, infoUrl, uploadUrl, deleteUrl, testid = "admin-video" }) {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = () => api.get(infoUrl).then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file (MP4)"); e.target.value = ""; return; }
    if (file.size > 200 * 1024 * 1024) { toast.error("Video exceeds the 200MB limit"); e.target.value = ""; return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(uploadUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      toast.success("Video uploaded — it now plays on the welcome page");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async () => {
    try { await api.delete(deleteUrl); toast.success("Video removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`${testid}-card`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100"><Video className="h-4 w-4 text-amber-600" /></span>
          <div>
            <h3 className="font-display font-bold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500" data-testid={`${testid}-status`}>
              {info?.available ? `Live: ${info.filename} (${Math.round((info.size || 0) / 1048576)} MB)` : "No video uploaded yet — the welcome page shows a placeholder."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {info?.available && <Button size="sm" variant="ghost" onClick={remove} className="text-red-600 hover:bg-red-50" data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4" /></Button>}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800" data-testid={`${testid}-upload-label`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? `Uploading ${progress}%` : (info?.available ? "Replace" : "Upload MP4")}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} disabled={uploading} data-testid={`${testid}-input`} />
          </label>
        </div>
      </div>
      {info?.available && <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Playing on the welcome page</div>}
    </div>
  );
}
